/**
 * Карточка заказа с информацией.
 */

import { formatRub as formatPrice } from '../../../shared/formatMoney.js';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  pending_payment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  pending_confirmation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  ready: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  refunded: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
};

const ORDER_TYPE_ICONS = {
  purchase: '🛒',
  booking_no_prepayment: '📅',
  booking_with_prepayment: '📅💳',
  universal: '📋',
};

const PAYMENT_STATUS_LABELS = {
  pending: 'Ожидает оплаты',
  processing: 'Оплачено',
  succeeded: 'Оплачено',
  failed: 'Ошибка оплаты',
  refunded: 'Средства возвращены',
  cancelled: 'Оплата отменена',
};

const getPaymentStatusLabel = (paymentStatus, orderType) => {
  if (paymentStatus === 'refunded' && orderType === 'booking_no_prepayment') {
    return 'Оплата отменена';
  }
  return PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus;
};

const PAYMENT_STATUS_COLORS = {
  pending: 'text-yellow-600 dark:text-yellow-400',
  processing: 'text-blue-600 dark:text-blue-400',
  succeeded: 'text-green-600 dark:text-green-400',
  failed: 'text-red-600 dark:text-red-400',
  refunded: 'text-orange-600 dark:text-orange-400',
  cancelled: 'text-gray-600 dark:text-gray-400',
};

export function OrderCard({ order }) {
  const statusColor = STATUS_COLORS[order.status] || STATUS_COLORS.draft;

  /**
   * Форматирует дату и время (для created_at, cancelled_at, completed_at).
   * Пример: "10 марта 2026, 15:30"
   */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    
    // Дата с названием месяца
    const dateFormatted = date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    
    // Время
    const timeFormatted = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
    
    return `${dateFormatted}, ${timeFormatted}`;
  };

  /**
   * Форматирует дату бронирования.
   * Пример: "15 марта 2026"
   */
  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '-';
    
    // Парсим дату в формате ISO (YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  /**
   * Форматирует время бронирования.
   * Пример: "19:00"
   */
  const formatBookingTime = (timeStr) => {
    if (!timeStr) return '-';
    
    // Убираем секунды из времени (HH:MM:SS -> HH:MM)
    const [hours, minutes] = timeStr.split(':');
    return `${hours}:${minutes}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">Заказ</p>
            <h1 className="text-2xl font-bold text-white">#{order.id}</h1>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusColor}`}>
            {order.status_display}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Тип заказа */}
        {order.order_type !== 'universal' && (
        <div className="flex items-center gap-3">
          <span className="text-2xl">{ORDER_TYPE_ICONS[order.order_type] || '📋'}</span>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Тип</p>
            <p className="font-medium">{order.order_type_display}</p>
          </div>
        </div>
        )}

        {/* Заведение */}
        <div className="flex items-start gap-3">
          <span className="text-2xl">📍</span>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Заведение</p>
            <p className="font-medium">{order.company_name}</p>
          </div>
        </div>

        {/* Покупатель */}
        <div className="flex items-start gap-3">
          <span className="text-2xl">👤</span>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Покупатель</p>
            <p className="font-medium">{order.customer_name || 'Не указано'}</p>
            {order.customer_phone && (
              <a href={`tel:${order.customer_phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                {order.customer_phone}
              </a>
            )}
          </div>
        </div>

        {/* Желаемое время получения (для покупок) */}
        {order.order_type === 'purchase' && (order.pickup_date || order.pickup_time) && (
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Желаемое время получения</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {order.pickup_date && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Дата</p>
                      <p className="font-medium">{formatBookingDate(order.pickup_date)}</p>
                    </div>
                  )}
                  {order.pickup_time && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Время</p>
                      <p className="font-medium">{formatBookingTime(order.pickup_time)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Комментарий покупателя */}
        {/* {order.comment && (
          <div className="flex items-start gap-3">
            <span className="text-2xl">💬</span>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Пожелания</p>
              <p className="text-gray-700 dark:text-gray-300">{order.comment}</p>
            </div>
          </div>
        )} */}

        {/* Причина отмены (если заказ отменён) */}
        {order.status === 'cancelled' && order.cancel_reason && (
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">❌</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                  Причина отмены
                  {order.cancelled_by && (
                    <span className="ml-2 text-xs font-normal text-red-600 dark:text-red-500">
                      ({order.cancelled_by === 'client' ? 'отменил покупатель' : 'отменил администратор'})
                    </span>
                  )}
                </p>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {order.cancel_reason}
                </p>
                {order.cancelled_at && (
                  <p className="text-xs text-red-600 dark:text-red-500 mt-2">
                    Отменён {formatDateTime(order.cancelled_at)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Бронирование */}
        {order.booking_details && (
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">📅 Бронирование</h3>
              {order.order_type === 'booking_no_prepayment' && (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 font-medium">
                  Без предоплаты
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Дата</p>
                <p className="font-medium">{formatBookingDate(order.booking_details.booking_date)}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Время</p>
                <p className="font-medium">{formatBookingTime(order.booking_details.booking_time)}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Гостей</p>
                <p className="font-medium">{order.booking_details.guests_count}</p>
              </div>
              {(order.booking_details.full_deposit_amount || order.booking_details.deposit_amount) && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Депозит</p>
                  <p className="font-medium">{formatPrice(order.booking_details.full_deposit_amount || order.booking_details.deposit_amount)}</p>
                </div>
              )}
              {order.booking_details.deposit_description && (
                <div className="col-span-2">
                  <p className="text-gray-500 dark:text-gray-400">Описание депозита</p>
                  <p className="font-medium">{order.booking_details.deposit_description}</p>
                </div>
              )}
            </div>
            {order.booking_details.comment && (
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <span className="text-sm">💬</span>
                  <div>
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">Пожелания к бронированию</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{order.booking_details.comment}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Универсальный заказ */}
        {order.order_type === 'universal' && order.admin_info && (
          <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
              <span>📋</span> Состав заказа
            </h3>
            <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
              {order.admin_info}
            </p>
          </div>
        )}

        {/* Товары */}
        {order.order_type !== 'universal' && order.items && order.items.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">🛒 Товары</h3>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-3 items-center py-2 border-b border-gray-200 dark:border-gray-600 last:border-0">
                  {/* Изображение товара */}
                  {item.product_image ? (
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0 bg-gray-200 dark:bg-gray-600"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center rounded-lg bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                      <span className="text-2xl">📦</span>
                    </div>
                  )}
                  {/* Информация о товаре */}
                  <div className="flex-1">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">× {item.quantity}</p>
                  </div>
                  <p className="font-semibold">{formatPrice(item.total_price)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Суммы — не показываем для бронирования без предоплаты */}
        {order.order_type !== 'booking_no_prepayment' && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
            {/* Для бронирования с депозитом: показываем полную сумму депозита */}
            {order.order_type === 'universal' ? (
              <>
                {order.original_amount > 0 && order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Стоимость без скидки:</span>
                    <span className="line-through">{formatPrice(order.original_amount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Итого:</span>
                  <span className="text-blue-600 dark:text-blue-400">{formatPrice(order.total_amount)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                  <span>К оплате онлайн:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatPrice(order.amount_to_pay)}</span>
                </div>
                {order.paid_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Оплачено:</span>
                    <span className="text-green-600 dark:text-green-400">{formatPrice(order.paid_amount)}</span>
                  </div>
                )}
              </>
            ) : order.order_type === 'booking_with_prepayment' && order.booking_details?.full_deposit_amount ? (
              <>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Депозит:</span>
                  <span className="text-blue-600 dark:text-blue-400">{formatPrice(order.booking_details.full_deposit_amount)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                {order.paid_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>💳 Оплачено покупателем:</span>
                    <span className="text-green-600 dark:text-green-400">{formatPrice(order.paid_amount)}</span>
                  </div>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Покупатель получит услуг на {formatPrice(order.booking_details.full_deposit_amount)}
                </p>
              </>
            ) : (
              <>
                {/* Для покупок со скидкой */}
                {order.original_amount > 0 && order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Цена:</span>
                    <span className="line-through">{formatPrice(order.original_amount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>🏷 Скидка:</span>
                    <span className="text-green-600 dark:text-green-400">−{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Итого:</span>
                  <span className="text-blue-600 dark:text-blue-400">{formatPrice(order.total_amount)}</span>
                </div>
                {order.paid_amount > 0 && (
                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                    <span>Оплачено:</span>
                    <span className="text-green-600 dark:text-green-400">{formatPrice(order.paid_amount)}</span>
                  </div>
                )}
              </>
            )}
            {order.payment_status?.has_payment && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm">
                  💳 Статус оплаты:{' '}
                  <span className={PAYMENT_STATUS_COLORS[order.payment_status.status] || 'text-gray-600 dark:text-gray-400'}>
                    {getPaymentStatusLabel(order.payment_status.status, order.order_type)}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Метаданные */}
        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
          <p>Создан: {formatDateTime(order.created_at)}</p>
          {order.completed_at && <p>Завершён: {formatDateTime(order.completed_at)}</p>}
        </div>
      </div>
    </div>
  );
}
